import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { View, ViewType } from 'api/views/types';
import { Filter } from 'components/molecules/filters/types';
import { toastService } from 'components/molecules/toast';
import { getViewTranformer } from 'components/molecules/views';
import { empty } from 'components/molecules/views/core';
import { useCurrentView } from 'components/molecules/views/current-view-state';
import { useTranslate } from 'hooks/use-translation';
import { useUser } from 'hooks/use-user';
import {
  PROJECT_TASK_VIEWS_QUERY_KEY,
  TASK_VIEWS_QUERY_KEY,
  useTaskViewsQuery,
} from 'queries/task-view';
import { TaskField } from 'stores/fields';
import { queueMacrotask } from 'utils/macrotask';
import { ColumnOrder } from '../grid-custom-column';
import { useColumnItems, useRowItems } from '../kanban-board/group-by';
import { FilterableField } from '../tasks/fields';
import {
  computeProjectDefaultTaskViews,
  getViewDefinitionTransformer,
  TasksViewDefinition,
} from '../tasks/views';
import { computeDefaultTaskViews } from '../tasks/views/default-views';
import { UseFilterMenuProps } from './types';
import { GroupBy } from './use-group-by-hash';

type Props = {
  isFieldsLoading: boolean;
  fields: FilterableField[];
  lookup: Record<string, FilterableField>;
  selectFilter: (filter: Filter) => void;
  clearFilters: () => void;
  setKanbanBoardGroupChangeLoading?: (value: boolean) => void;
  groupBy?: GroupBy;
  setGroupBy?: (value: GroupBy, replace?: boolean) => void;
  projectId?: string;
  handleColumnOrderChange?: (changedOrder: ColumnOrder[]) => void;
  defaultColumnOrder?: ColumnOrder[];
};

export const useFilterMenu = ({
  fields,
  lookup,
  isFieldsLoading,
  setKanbanBoardGroupChangeLoading,
  selectFilter,
  clearFilters,
  setGroupBy,
  projectId,
  handleColumnOrderChange,
  defaultColumnOrder = [],
}: Props): UseFilterMenuProps => {
  const translate = useTranslate();
  const [currentEditingView, setCurrentEditingView] = useState<View<any>>();
  const [manageViewModalVisible, setManageViewModalVisible] = useState(false);
  const {
    user: { userId },
  } = useUser();
  const kanbanBoardRowItems = useRowItems(fields as TaskField[]);
  const kanbanBoardColumnItems = useColumnItems(fields as TaskField[]);
  const viewDefinitionTransformer = useMemo(
    () =>
      getViewDefinitionTransformer(
        lookup,
        kanbanBoardRowItems,
        kanbanBoardColumnItems,
        defaultColumnOrder,
      ),
    [lookup, kanbanBoardRowItems, kanbanBoardColumnItems, defaultColumnOrder],
  );
  const viewTransformer = useMemo(
    () => getViewTranformer(viewDefinitionTransformer),
    [viewDefinitionTransformer],
  );
  const [defaultViewsList, defaultViewId] = useMemo(() => {
    if (projectId) {
      return computeProjectDefaultTaskViews({
        lookup,
        userId,
        isFieldsLoading,
        translate,
        defaultColumnOrder,
      });
    }

    return computeDefaultTaskViews({
      lookup,
      userId,
      isFieldsLoading,
      translate,
      defaultColumnOrder,
    });
  }, [
    projectId,
    lookup,
    userId,
    isFieldsLoading,
    translate,
    defaultColumnOrder,
  ]);

  const { data: taskListViews = empty, isLoading: isViewsLoading } =
    useTaskViewsQuery({
      enabled: !isFieldsLoading,
      viewTransformer,
      projectId,
    });

  const { viewId: currentViewId, setViewId: setCurrentViewId } = useCurrentView(
    {
      defaultViewId,
    },
  );

  const selectedViewWithState = useMemo(
    () =>
      taskListViews.byIds[currentViewId] ??
      defaultViewsList.byIds[currentViewId] ??
      defaultViewsList.byIds[defaultViewId],
    [defaultViewId, currentViewId, taskListViews.byIds, defaultViewsList.byIds],
  );

  const selectedView = useMemo(() => {
    if (!selectedViewWithState) {
      return undefined;
    }

    return selectedViewWithState[0] === 'updating'
      ? selectedViewWithState[2]
      : selectedViewWithState[1];
  }, [selectedViewWithState]);

  const onViewEdit = (view) => {
    setCurrentEditingView(view);
    setManageViewModalVisible(true);
  };

  const didFirstLoadComplete = useRef(false);

  useEffect(() => {
    if (
      currentViewId &&
      currentViewId > 0 &&
      selectedView &&
      selectedView.viewId > 0 &&
      !didFirstLoadComplete.current
    ) {
      if (selectedView && selectedView?.viewId === currentViewId) {
        didFirstLoadComplete.current = true;
        handleColumnOrderChange?.(
          selectedView?.viewDefinition?.columnOrder ?? defaultColumnOrder,
        );
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentViewId, selectedView]);

  const onViewSelect = useCallback(
    ({ viewId, viewDefinition }, isClearAll?: boolean) => {
      if (setKanbanBoardGroupChangeLoading) {
        setKanbanBoardGroupChangeLoading(true);
      }
      const nextFilter = (viewDefinition as TasksViewDefinition).filter;

      if (nextFilter && nextFilter.criteria.length) {
        selectFilter(nextFilter);
      } else {
        clearFilters();
      }

      const lsOrder = JSON.parse(
        localStorage.getItem(
          `project_${projectId}.task_list.v3.view=${viewId}column_order`,
        ) || '[]',
      );

      if (lsOrder && lsOrder.length > 0) {
        localStorage.removeItem(
          `project_${projectId}.task_list.v3.view=${viewId}column_order`,
        );
      }

      queueMacrotask(() => {
        setCurrentViewId(viewId, true);
        setGroupBy?.(viewDefinition.groupBy, true);
        if (setKanbanBoardGroupChangeLoading) {
          setKanbanBoardGroupChangeLoading(false);
        }
        if (defaultViewsList.ids[1]?.includes(viewId)) {
          handleColumnOrderChange?.(defaultColumnOrder);
        } else if (isClearAll) {
          handleColumnOrderChange?.(
            viewDefinition.columnOrder ?? defaultColumnOrder,
          );
        } else {
          handleColumnOrderChange?.(
            viewDefinition.columnOrder ?? defaultColumnOrder,
          );
        }
      });
    },
    [
      clearFilters,
      defaultColumnOrder,
      defaultViewsList.ids,
      handleColumnOrderChange,
      projectId,
      selectFilter,
      setCurrentViewId,
      setGroupBy,
      setKanbanBoardGroupChangeLoading,
    ],
  );

  const onDeleteSuccess = useCallback(
    (view) => {
      toastService({
        title: translate('success'),
        message: translate('views.delete.success', {
          viewName: view.viewName,
        }),
      }).success();
    },
    [translate],
  );

  const onDeleteFailure = useCallback(
    (view) => {
      toastService({
        title: translate('failure'),
        message: translate('views.delete.failed', {
          viewName: view.viewName,
        }),
      }).error();
    },
    [translate],
  );

  const viewContextValue = {
    queryKey: projectId
      ? [PROJECT_TASK_VIEWS_QUERY_KEY, projectId]
      : TASK_VIEWS_QUERY_KEY,
    viewSourceId: projectId ? Number(projectId) : undefined,
    viewType: projectId ? ViewType.ProjectTaskList : ViewType.AllTasks,
  };

  return {
    taskListViews,
    viewDefinitionTransformer,
    currentViewId,
    viewContextValue,
    onViewSelect,
    defaultViewsList,
    defaultViewId,
    manageViewModalVisible,
    setManageViewModalVisible,
    setCurrentEditingView,
    currentEditingView,
    selectedView,
    onViewEdit,
    isViewsLoading,
    onDeleteSuccess,
    onDeleteFailure,
  };
};
